/* eslint-disable react-refresh/only-export-components */

import { useVariable } from '@softwareimaging/backstage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { Metadata } from '../../../../sidecar/lists/lib/list';
import { Variables } from '../backstage/config';
import { useGraph } from '../sidecar/useGraphProvider';
import { Lists } from './client';

const ListsClientContext = createContext<Lists | null>(null);
export const ListsProvider: FC<PropsWithChildren> = ({ children }) => {
  const deploymentMetadata = useVariable<Variables>('listDeploymentMetadata');

  const { client } = useGraph();
  const lists = useMemo(() => {
    if (!deploymentMetadata) throw new Error('Missing deployment metadata');
    const metadata = Metadata.parse(JSON.parse(deploymentMetadata));

    return new Lists(client, metadata);
  }, [client, deploymentMetadata]);

  return <ListsClientContext.Provider value={lists}>{children}</ListsClientContext.Provider>;
};

export function useLists() {
  const lists = useContext(ListsClientContext);
  if (!lists) throw new Error('Lists context not found');
  return lists;
}

type ListQueryBuilder<T> = (lists: Lists) => {
  keys: unknown[];
  execute: () => Promise<T>;
  siteId: string;
  listId: string;
};

export function useListQuery<T>(fn: ListQueryBuilder<T>) {
  const lists = useLists();
  const query = fn(lists);
  const value = useQuery(query.keys, query.execute);
  if (!value.data) throw new Error('Query not found');
  return [
    value.data,
    {
      ...value,
      siteId: query.siteId,
      listId: query.listId
    }
  ] as const;
}

interface MutationConfiguration<T> {
  keys: unknown[];
  invalidatedKeys?: unknown[];
  execute: () => Promise<T>;
}

type ListMutationBuilder<T, A> = (
  lists: Lists,
  args: A
) => MutationConfiguration<T> | Promise<MutationConfiguration<T>>;

export function useListMutation<T, A>(fn: ListMutationBuilder<T, A>) {
  const lists = useLists();
  const client = useQueryClient();
  return useMutation<T, unknown, A>({
    mutationFn: async args => {
      const mutation = await fn(lists, args);
      const result = await mutation.execute();
      if (mutation.invalidatedKeys) {
        // If you to guarantee the app is in a good state with all relevant data cleared up then await this
        client.invalidateQueries({ queryKey: mutation.invalidatedKeys }).catch(console.error);
      }
      return result;
    }
  });
}
